// SupportServices.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, BookOpen, Calendar, FileText, FolderOpen, CheckCircle2 } from 'lucide-react';

const SupportServices = () => {
  const services = [
    {
      icon: Globe,
      title: 'Shortlisting Countries and Schools',
      description: 'Options drawn from your academic record and where you want your career to go',
      color: 'blue'
    },
    {
      icon: BookOpen,
      title: 'Course Fit and Intake Advice',
      description: 'Help choosing a programme that matches you, and the intake worth aiming for',
      color: 'purple'
    },
    {
      icon: Calendar,
      title: 'Planning and Deadline Tracking',
      description: 'A working schedule that keeps each application moving on time',
      color: 'green'
    },
    {
      icon: FileText,
      title: 'Statement of Purpose Drafting',
      description: 'Your personal statement worked over until it meets what institutions expect',
      color: 'orange'
    },
    {
      icon: FolderOpen,
      title: 'Paperwork and Filing',
      description: 'Every document put in order and lodged where it needs to go',
      color: 'red'
    },
    {
      icon: CheckCircle2,
      title: 'Follow-ups and What Comes Next',
      description: 'Continued help from submission through to the offer and past it',
      color: 'teal'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-t-blue-500',
      purple: 'border-t-purple-500',
      green: 'border-t-green-500',
      orange: 'border-t-orange-500',
      red: 'border-t-red-500',
      teal: 'border-t-teal-500'
    };
    return colors[color] || 'border-t-blue-500';
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What Our Support Covers
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            We stay with you across the whole admissions process. The emphasis is on preparing properly, getting the detail right, and keeping things plain.
          </p>
        </motion.div>

        {/* Service Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-white p-6 border-t-2 ${getColorClasses(service.color)} hover:shadow-lg transition-shadow`}
              >
                <Icon size={32} className="text-gray-700 mb-4" />
                
                <h3 className="text-lg font-semibold text-primary mb-3 leading-tight">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SupportServices;
